import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import type { SlideProps } from '@/components/SlideWizard'
import { useSlideCopy } from '@/lib/i18n'
import { copy } from './Slide05YouveNoticed.copy'

const ease = [0.16, 1, 0.3, 1] as const
const transitionBase = { duration: 0.45, ease }
const animationProps = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 } }

const TOTAL_DOTS = 100
const FILLED_DOTS = 57
const ROWS = 5
const COLS = Math.ceil(TOTAL_DOTS / ROWS)

// Count-up timing is chosen to feel in sync with the red dots filling in:
// dots start animating at delay 0.3s and stagger through ~0.46s of fills,
// so a 1.1s count-up that kicks off at 0.3s lands on 57 at roughly the same
// moment the last red dot pops in.
const COUNTUP_START_MS = 300
const COUNTUP_DURATION_MS = 1100

function useCountUp(target: number, durationMs: number, startAfterMs: number) {
  const [value, setValue] = useState(0)

  useEffect(() => {
    let rafId = 0
    let startTime = 0

    const tick = (now: number) => {
      if (!startTime) startTime = now
      const elapsed = now - startTime
      const progress = Math.min(1, elapsed / durationMs)
      // easeOutCubic: fast-start, soft-landing
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(target * eased))
      if (progress < 1) rafId = requestAnimationFrame(tick)
    }

    const timer = setTimeout(() => {
      rafId = requestAnimationFrame(tick)
    }, startAfterMs)

    return () => {
      clearTimeout(timer)
      cancelAnimationFrame(rafId)
    }
  }, [target, durationMs, startAfterMs])

  return value
}

export default function Slide05YouveNoticed(_props: SlideProps) {
  const c = useSlideCopy(copy)
  const animatedPercent = useCountUp(FILLED_DOTS, COUNTUP_DURATION_MS, COUNTUP_START_MS)

  // Split body around the {emphasis} sentinel. The emphasized portion renders
  // in darker weight and contains the animated percentage number.
  const [beforeEmphasis, afterEmphasis] = c.body.split('{emphasis}')

  return (
    <section className="text-slate-900 min-h-[70vh] w-full flex flex-col justify-center">
      {/* Title */}
      <motion.div
        {...animationProps}
        transition={{ ...transitionBase, delay: 0 }}
        className="mb-0 max-w-xl text-center mx-auto"
      >
        <h2 className="text-5xl sm:text-6xl font-semibold text-slate-900 tracking-tight whitespace-pre-line leading-[1.1] text-center">
          {c.title}
        </h2>
      </motion.div>

      {/* Down-arrow separator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ ...transitionBase, delay: 0.15 }}
        className="flex justify-center py-8"
      >
        <svg
          width="24"
          height="40"
          viewBox="0 0 24 40"
          fill="none"
          className="text-slate-400"
        >
          <path
            d="M12 0 L12 34 M5 27 L12 34 L19 27"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </motion.div>

      {/* Stat sentence with animated percentage */}
      <motion.p
        {...animationProps}
        transition={{ ...transitionBase, delay: 0.1 }}
        className="text-2xl sm:text-3xl mb-2 leading-snug text-slate-600 max-w-xl text-center mx-auto"
      >
        {beforeEmphasis}
        <span className="text-slate-900 font-semibold">
          <span className="tabular-nums">{animatedPercent}</span>
          {c.emphasisSuffix}
        </span>
        {afterEmphasis}
      </motion.p>

      {/* Dot grid */}
      <motion.div
        {...animationProps}
        transition={{ ...transitionBase, delay: 0.2 }}
        className="mt-4"
      >
        <div className="flex flex-col items-center gap-6">
          <div
            className="grid gap-[6px] sm:gap-2"
            style={{ gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))` }}
            aria-label={c.dotsAriaLabel
              .replace('{filled}', String(FILLED_DOTS))
              .replace('{total}', String(TOTAL_DOTS))}
          >
            {Array.from({ length: ROWS * COLS }, (_, i) => i).map((i) => (
              <motion.div
                key={i}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: i < TOTAL_DOTS ? 1 : 0 }}
                transition={{
                  duration: 0.3,
                  delay: 0.3 + i * 0.008,
                  ease,
                }}
                className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full ${
                  i < FILLED_DOTS ? 'bg-red-500' : 'bg-slate-300'
                }`}
              />
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  )
}
